import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { formatDateTime, labelText } from "../lib/format";
import { Loading, ErrorState } from "../components/ui";
import { VisitModal } from "../components/VisitModal";
import { classifyVisit, isUpcomingVisit, type VisitBucket } from "@championcare/ui";
import type { AppointmentView, DoctorSummary } from "@forcebase-ai/schema/patient";

const dayNum = (iso: string) => new Date(iso).getDate();
const monthAbbr = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: "short" }).toUpperCase();

// Map the shared visit bucket onto the existing status-pill CSS classes.
const BUCKET_CLASS: Record<VisitBucket, string> = {
  completed: "completed",
  cancelled: "canceled",
  missed: "missed",
  live: "upcoming",
  upcoming: "upcoming",
};

function Row({ a, doctor, onOpen }: { a: AppointmentView; doctor: DoctorSummary | undefined; onOpen: (a: AppointmentView) => void }) {
  // Bucket via the shared classifier so Upcoming/Past + the "Missed" label match every other surface.
  const bucket = classifyVisit(a);
  const statusClass = BUCKET_CLASS[bucket];
  const statusLabel = bucket === "missed" ? "Missed" : bucket === "live" ? "Live" : a.status;
  return (
    <button type="button" className="visit-row" onClick={() => onOpen(a)}>
      <div className="visit-date">
        <div className="day">{dayNum(a.scheduled_at)}</div>
        <div className="month">{monthAbbr(a.scheduled_at)}</div>
      </div>
      <div className="visit-info">
        <div className="visit-doctor">
          {doctor ? `Dr. ${doctor.first_name} ${doctor.last_name}` : "Your visit"}
        </div>
        <div className="visit-detail-text">
          <span>{formatDateTime(a.scheduled_at)}</span>
          <span>{labelText(a.label)}</span>
        </div>
      </div>
      <span className={`visit-status ${statusClass}`}>{statusLabel}</span>
    </button>
  );
}

export function Visits() {
  const api = useApi();
  // Poll so async transitions (EMR "Syncing…"→"Confirmed", scheduled→live) land without a refresh.
  const visits = useAsync(() => api.listAppointments(), [], { pollMs: 10000 });
  const doctors = useAsync(() => api.listDoctors(), []);
  const [tab, setTab] = useState<"upcoming" | "past">("upcoming");
  const [openAppt, setOpenAppt] = useState<AppointmentView | null>(null);

  if (visits.loading) return <Loading />;
  if (visits.error) return <ErrorState message={visits.error} />;

  const all = visits.data ?? [];
  const byId = new Map((doctors.data ?? []).map((d) => [d.id, d]));
  const now = Date.now();
  const upcoming = all
    .filter((a) => isUpcomingVisit(a, now))
    .sort((a, b) => +new Date(a.scheduled_at) - +new Date(b.scheduled_at));
  const past = all
    .filter((a) => !isUpcomingVisit(a, now))
    .sort((a, b) => +new Date(b.scheduled_at) - +new Date(a.scheduled_at));
  const rows = tab === "upcoming" ? upcoming : past;

  return (
    <>
      <div className="app-page-head">
        <h1>Your <em>visits.</em></h1>
        <p>Upcoming appointments and past care history.</p>
      </div>

      <div className="tab-pills" role="tablist">
        <button
          className={`tab-pill ${tab === "upcoming" ? "active" : ""}`}
          onClick={() => setTab("upcoming")}
          role="tab"
          aria-selected={tab === "upcoming"}
        >
          Upcoming
        </button>
        <button
          className={`tab-pill ${tab === "past" ? "active" : ""}`}
          onClick={() => setTab("past")}
          role="tab"
          aria-selected={tab === "past"}
        >
          Past
        </button>
      </div>

      {rows.length ? (
        <div className="visit-list">
          {rows.map((a) => (
            <Row key={a.id} a={a} doctor={byId.get(a.doctor_id)} onOpen={setOpenAppt} />
          ))}
        </div>
      ) : (
        <div className="pt-empty">
          {tab === "upcoming" ? (
            <>
              No upcoming visits. <Link to="/visits/book">Book a visit</Link> when you need care.
            </>
          ) : (
            "No past visits yet."
          )}
        </div>
      )}

      {openAppt && (
        <VisitModal
          appt={openAppt}
          doctor={byId.get(openAppt.doctor_id)}
          onClose={() => setOpenAppt(null)}
          onChanged={() => visits.reload()}
        />
      )}
    </>
  );
}
